/**
 * Вставка выбранного упоминания в черновик + постановка каретки ПОСЛЕ
 * коммита React-состояния (Антон, 2026-10-16 09:50, «на iOS и Mac одинаково»).
 *
 * БАГ: после выбора из подсказки @ каретка оставалась посреди вставленного
 * имени. Причины, которые закрывает этот модуль:
 *
 *  1. Конец заменяемого `@запроса` брался из `textarea.selectionStart` в
 *     момент выбора. В WKWebView (Mac-оболочка) при активном inline-предиктиве
 *     / marked text это значение не совпадает с концом набранного запроса,
 *     и замена резала текст не там. Теперь конец считается из состояния:
 *     `start + 1 + query.length` — ровно то, что лежит в `text`.
 *  2. Каретка ставилась одним `requestAnimationFrame` из обработчика клика.
 *     React-коммит `value` и асинхронное восстановление выделения движком
 *     (WebKit после compositionend / смены value) могли прийти ПОЗЖЕ и
 *     вернуть каретку на старую позицию. Теперь позиция ждёт коммита
 *     (`useLayoutEffect` по `text`) и повторно подтверждается на следующих
 *     кадрах и на `compositionend`, пока пользователь сам не начал ввод.
 *  3. Пробел после имени: ставится ровно один — если сразу после запроса
 *     уже стоит пробел/перенос, новый не добавляется, каретка встаёт за ним.
 */
import { useCallback, useLayoutEffect, useRef, useState } from "react";

export interface MentionInsertResult {
  /** Новый текст черновика. */
  text: string;
  /** Позиция каретки: за именем и ровно одним пробелом. */
  caret: number;
  /** Где начинается заменённый участок (для сдвига диапазонов форматирования). */
  replaceStart: number;
  /** Где кончался заменённый участок в СТАРОМ тексте. */
  replaceEnd: number;
  /** На сколько сдвинулся текст после replaceEnd. */
  delta: number;
}

/**
 * Чистая функция: заменить `@запрос` (от `start` длиной 1 + query.length)
 * на `displayName` и гарантировать ровно один пробел после него.
 */
export function insertMention(
  text: string,
  start: number,
  query: string,
  displayName: string,
): MentionInsertResult {
  const replaceStart = Math.max(0, Math.min(start, text.length));
  const replaceEnd = Math.min(text.length, replaceStart + 1 + query.length);
  const before = text.slice(0, replaceStart);
  const after = text.slice(replaceEnd);
  // Пробел уже есть (или перенос строки) — второй не дописываем.
  const hasSpace = after.length > 0 && (after[0] === " " || after[0] === "\n" || after[0] === "\u00a0");
  const insert = hasSpace ? displayName : `${displayName} `;
  const next = `${before}${insert}${after}`;
  const caret = before.length + displayName.length + 1;
  return {
    text: next,
    caret,
    replaceStart,
    replaceEnd,
    delta: insert.length - (replaceEnd - replaceStart),
  };
}

/**
 * Хук: «поставь каретку сюда, когда React закоммитит новый `value`».
 *
 * `request(pos)` вызывается в момент выбора упоминания вместе с setText.
 * Позиция применяется в useLayoutEffect после коммита (до отрисовки), затем
 * подтверждается ещё на двух кадрах — WebKit иногда восстанавливает старое
 * выделение асинхронно. `compositionend` и любой пользовательский ввод
 * вызывают `confirm()` / `cancel()` соответственно, чтобы не драться с
 * человеком, который уже печатает дальше.
 */
export function useCaretAfterCommit(
  ref: React.RefObject<HTMLTextAreaElement | null>,
  text: string,
) {
  const pending = useRef<number | null>(null);
  const generation = useRef(0);
  // Отдельный счётчик рендера: если выбранное имя совпало с уже набранным
  // («@Вася » → «@Вася»), `text` не меняется и эффект по нему не сработал бы.
  const [tick, setTick] = useState(0);

  const apply = useCallback(() => {
    const el = ref.current;
    const pos = pending.current;
    if (!el || pos === null) return;
    // value ещё не обновлён — ждём коммита, не ставим каретку в чужой текст.
    if (el.value.length < pos) return;
    if (document.activeElement !== el) el.focus();
    if (el.selectionStart !== pos || el.selectionEnd !== pos) {
      el.setSelectionRange(pos, pos);
    }
  }, [ref]);

  const request = useCallback((pos: number) => {
    pending.current = pos;
    generation.current += 1;
    setTick((n) => n + 1);
  }, []);

  const cancel = useCallback(() => {
    pending.current = null;
    generation.current += 1;
  }, []);

  useLayoutEffect(() => {
    if (pending.current === null) return;
    const gen = generation.current;
    apply();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      if (generation.current !== gen) return;
      apply();
      raf2 = requestAnimationFrame(() => {
        if (generation.current !== gen) return;
        apply();
        pending.current = null;
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [text, tick, apply]);

  /** compositionend после выбора: WebKit мог вернуть каретку — ставим ещё раз. */
  const confirm = useCallback(() => {
    if (pending.current === null) return;
    apply();
    const gen = generation.current;
    requestAnimationFrame(() => {
      if (generation.current === gen) apply();
    });
  }, [apply]);

  return { request, cancel, confirm };
}
